import { API_CONFIG } from '../config/ApiConfig';

// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Note: Authentication types are now defined in AuthenticationService.ts

// API Service Class
export class ApiService {
  private apiKey: string;

  constructor() {
    this.apiKey = API_CONFIG.API_KEY;
  }

  // Helper method to get the appropriate base URL for different services
  private getBaseURL(service: 'main' | 'ai' = 'main'): string {
    return service === 'ai' ? API_CONFIG.AI_BASE_URL : API_CONFIG.BASE_URL;
  }

  // Helper method to create headers
  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': '*/*',
    };

    if (includeAuth) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('API Request with token:', token.substring(0, 20) + '...');
      } else {
        console.warn('No auth token found in localStorage');
      }
    }

    // Add API key if needed
    if (this.apiKey) {
      headers['X-Api-Key'] = this.apiKey;
    }

    return headers;
  }

  // Helper method to create form data
  private createFormData(data: Record<string, unknown>): URLSearchParams {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });
    return formData;
  }

  // Generic POST method
  // async post<T>(
  //   endpoint: string,
  //   data: Record<string, unknown>,
  //   includeAuth: boolean = true,
  //   service: 'main' | 'ai' = 'main'
  // ): Promise<T> {
  //   const url = `${this.getBaseURL(service)}${endpoint}`;
  //   const formData = this.createFormData(data);
    
  //   const response = await fetch(url, {
  //     method: 'POST',
  //     headers: this.getHeaders(includeAuth),
  //     body: formData,
  //   });

  //   if (!response.ok) {
  //     const errorData = await response.json().catch(() => ({}));
  //     throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  //   }

  //   const result = await response.json();
  //   return result;
  // }

  // async post<T>(
  //   endpoint: string,
  //   data: Record<string, any>,
  //   includeAuth: boolean = true,
  //   service: 'main' | 'ai' = 'main'
  // ): Promise<T> {
  //   const url = `${this.getBaseURL(service)}${endpoint}`;
  //   const formData = this.createFormData(data);

  //   const response = await fetch(url, {
  //     method: 'POST',
  //     headers: this.getHeaders(includeAuth),
  //     body: formData,
  //   });

  //   if (!response.ok) {
  //     const errorData = await response.json().catch(() => ({}));
  //     throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  //   }

  //   return response.json();
  // }

async post<T>(
  endpoint: string,
  data: Record<string, any>,
  includeAuth: boolean = true,
  service: 'main' | 'ai' = 'main'
): Promise<T> {

  const url = `${this.getBaseURL(service)}${endpoint}`;
  const formData = this.createFormData(data);

  const response = await fetch(url, {
    method: 'POST',
    headers: this.getHeaders(includeAuth),
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    let errorData: any = {};

    if (text.trim()) {
      try {
        errorData = JSON.parse(text);
      } catch {}
    }

    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const responseText = await response.text();

  if (!responseText.trim()) {
    return { status: response.status } as T;
  }

  return JSON.parse(responseText);
}




  // Generic GET method
  // async get<T>(endpoint: string, includeAuth: boolean = true, service: 'main' | 'ai' = 'main'): Promise<T> {
  //   const url = `${this.getBaseURL(service)}${endpoint}`;
    
  //   const response = await fetch(url, {
  //     method: 'GET',
  //     headers: this.getHeaders(includeAuth),
  //   });

  //   if (!response.ok) {
  //     const errorData = await response.json().catch(() => ({}));
  //     throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  //   }

  //   const result = await response.json();
  //   return result;
  // }


async get<T>(url: string, includeAuth = true, _service: "main" | "ai" = "main"): Promise<T> {
  const response = await fetch(url, {
    method: "GET",
    headers: this.getHeaders(includeAuth),
  });

  if (!response.ok) {
    // Try to read error JSON
    let errorData = null;
    try { 
      errorData = await response.json();
    } catch { 
      // ignore
    }
    throw new Error(errorData?.message || `HTTP error ${response.status}`);
  }

  // Check if response body is empty
  const text = await response.text();
  if (!text) return [] as T; // return empty array for Sharetribe listing querying

  try {
    return JSON.parse(text) as T;
  } catch {
    console.warn("Non-JSON response from GET:", text);
    return [] as T;
  }
}


  // Generic PUT method
  async put<T>(endpoint: string, data: Record<string, unknown>, includeAuth: boolean = true, service: 'main' | 'ai' = 'main'): Promise<T> {
    const url = `${this.getBaseURL(service)}${endpoint}`;
    const formData = this.createFormData(data);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(includeAuth),
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  }

  // Generic DELETE method
  async delete<T>(endpoint: string, includeAuth: boolean = true, service: 'main' | 'ai' = 'main'): Promise<T> {
    const url = `${this.getBaseURL(service)}${endpoint}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(includeAuth),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    const result = JSON.parse(text);
    return result;
  }

  // FormData POST method
  async postFormData<T>(endpoint: string, formData: FormData, includeAuth: boolean = true, service: 'main' | 'ai' = 'main'): Promise<T> {
    const url = `${this.getBaseURL(service)}${endpoint}`;
    
    const headers: HeadersInit = {
      'Accept': '*/*',
    };

    if (includeAuth) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    if (this.apiKey) {
      headers['X-Api-Key'] = this.apiKey;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  }

  // Get blob data (for file downloads)
  async getBlob(endpoint: string, includeAuth: boolean = true, service: 'main' | 'ai' = 'main'): Promise<Blob> {
    const url = `${this.getBaseURL(service)}${endpoint}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(includeAuth),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  // Note: Authentication methods are now handled by AuthenticationService

  // Token management
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  removeAuthToken(): void {
    localStorage.removeItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export the class for testing
//export { ApiService };
